import { computed, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';

export type Theme = 'light' | 'dark';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly THEME_KEY = 'bharatlaw-theme';

  private readonly _theme = signal<Theme>('light');

  // Signal API — components can read this directly in templates, no subscribe needed
  readonly theme = this._theme.asReadonly();
  readonly isDark = computed(() => this._theme() === 'dark');

  // Observable API — kept for backward-compat with any existing .subscribe() callers
  readonly theme$: Observable<Theme> = toObservable(this._theme);

  constructor() {
    this.initializeTheme();
  }

  private initializeTheme(): void {
    const savedTheme = localStorage.getItem(this.THEME_KEY) as Theme;
    this.setTheme(savedTheme || 'light');
  }

  get currentTheme(): Theme {
    return this._theme();
  }

  setTheme(theme: Theme): void {
    this._theme.set(theme);
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(this.THEME_KEY, theme);
  }

  toggleTheme(): void {
    this.setTheme(this._theme() === 'light' ? 'dark' : 'light');
  }

  isDarkTheme(): boolean {
    return this.isDark();
  }

  getThemeDisplayName(theme: Theme): string {
    return theme === 'dark' ? 'Dark' : 'Light';
  }

  getNextTheme(): Theme {
    return this._theme() === 'light' ? 'dark' : 'light';
  }
}

